/**
 * Prins (prins-grundsystem-2023)
 *
 * @copyright 2023 i-fabrik GmbH
 * @author heiko
 * @version v2.230726 (2023/07/26 14:47:19, header.js)
 */

import {getSelector, getUid} from '@ifabnpm/bootstrap/utils.js';
import {feature} from "@ifabnpm/bootstrap/utils/support.js";
import {
	SelectorEngine,
	Manipulator,
	EventHandler,
	Data
} from '@ifabnpm/bootstrap/dom.js';

import Spinner from '@ifabnpm/bootstrap/component/spinner.js';

// -------
// Privat
// -------

let notificationWorker = null;

// const notificationTemplate = `<div class="notification" data-notification="{{id}}" data-read="{{read}}">
// 	<div class="notification-inner">
// 		<i aria-hidden="true" data-icon class="fa-regular fa-bell notification__icon"></i>
// 		<p class="notification__text"><strong>{{title}}</strong>{{text}}</p>
// 		<button class="notification__read" data-bs-toggle="tooltip" data-bs-title="Mark as read" type="button">mark as read</button>
// 	</div>
// </div>`;
const notificationTemplate = `<div class="notification" data-notification="{{id}}" data-read="{{read}}">
	<div class="notification__inner">
		<div class="notification-title">{{title}}</div>
		<div class="notification-teaser"><p>{{teaser}}</p><p class="date">{{date}}</p></div>
		<button aria-label="Details" class="notification-trigger" data-details data-modal-title="{{title}}" data-target="#notification-content-{{id}}" type="button">
			<i aria-hidden="true" class="fa-solid fa-chevron-right" data-icon></i>
		</button>
		<!--<button aria-label="Entfernen" class="notification-remove" data-remove type="button">
			<i aria-hidden="true" data-icon class="fa-regular fa-times"></i>
		</button>-->
		<i aria-hidden="true" data-icon class="fa-regular fa-bell notification-icon"></i>
	</div>
	<div class="notification-details" id="notification-content-{{id}}"><div class="notification-content"><p class="date">{{date}}</p>{{text}}</div>
</div>`;

const notificationHintTemplate = `<div class="app-header-item notification-hint" data-notification-hint="{{id}}">
	<div class="notification-hint__inner">
		<div class="notification-hint-text">
				<span class="title">{{title}}</span>
				<span class="teaser">{{teaser}}</span>
		</div>
		<button aria-label="Details" type="button">&nbsp;</button>
		<i aria-hidden="true" class="fa-regular fa-circle-info" data-icon></i>
	</div>
</div>`;

const notificationParams = {
	dropdown         : null,
	dropdownTrigger  : null,
	notificationGroup: null,
	currentStorageData : {}
};

/**
 *
 * @param s
 * @param data
 * @param rp
 * @returns String
 */
const parseString = (s, data, rp = true) => {
	let replaced = s;

	for (let key in data) {
		if (data.hasOwnProperty(key) && data[key]) {
			replaced = replaced.replace(new RegExp('{{' + key + '}}', 'gm'), data[key]);
		}
	}

	if (rp) {
		replaced = replaced.replace(new RegExp('{{(.*?)}}', 'gm'), '');
	}

	return replaced;
};

/**
 *
 * @returns {Promise<any|{}>}
 */
const storageDataGet = async () => {
	const data = localStorage.getItem(`${window.Prins.prefix}notifications`);

	return data ? JSON.parse(data) : {};
};

/**
 *
 * @returns {Promise<void>}
 */
const updateAllStorageData = async () => {
	const collection = SelectorEngine.find(`[data-notification]`);

	let storageData = {};

	for (const element of collection) {
		storageData[element.dataset.notification] = {
			read   : (undefined !== element.dataset.read && element.dataset.read === 'true'),
			removed: (notificationParams.currentStorageData[element.dataset.notification] && notificationParams.currentStorageData[element.dataset.notification].removed)
		};
	}

	notificationParams.currentStorageData = storageData;

	localStorage.setItem(`${window.Prins.prefix}notifications`, JSON.stringify(storageData));
};

/**
 *
 * @param id
 */
const markNotificationAsRead = async (id) => {
	if (id) {
		const notifications = SelectorEngine.find(`[data-notification="${id}"]`)

		for (const item of notifications) {
			item.dataset.read = 'true';
		}

		await updateAllStorageData();
	}
};

/**
 *
 * @param id
 */
const markNotificationAsRemoved = async (id) => {
	if (id) {
		const notifications = SelectorEngine.find(`[data-notification="${id}"]`)

		for (const item of notifications) {
			item.remove();
		}

		if (!notificationParams.currentStorageData[id]) {
			notificationParams.currentStorageData[id] = {};
		}

		notificationParams.currentStorageData[id].removed = true;

		await updateAllStorageData();
	}
};

/**
 *
 * @returns {Promise<void>}
 */
const parseNotifications = async () => {
	const collection = SelectorEngine.find('[data-notification]');

	for (const element of collection) {
		if(!Data.get(element, 'notification.initialized')) {
			EventHandler.on(element, 'click.notification', 'button[data-details]', (event) => {
				event.preventDefault();
				event.stopPropagation();

				const notification = SelectorEngine.parents(event.target, '[data-notification]')[0];

				markNotificationAsRead(notification.dataset.notification);

				// Modalfunktionalität?
				const selector = getSelector(event.target);
				const modalTarget   = (selector) ? SelectorEngine.findOne(selector) : null;

				if (window.Prins.modalInfo && modalTarget) {
					Data.set(event.target, 'ifab.modal.target', modalTarget);

					window.Prins.modalInfo.show(event.target);
				}
			});

			EventHandler.on(element, 'click.notification', 'button[data-remove]', (event) => {
				event.preventDefault();
				event.stopPropagation();

				const notification = SelectorEngine.parents(event.target, '[data-notification]')[0];

				markNotificationAsRemoved(notification.dataset.notification);
			});

			Data.set(element, 'notification.initialized', true);
		}
	}
};

/**
 *
 * @returns {Promise<void>}
 */
const setupNotificationGroup = async () => {
	EventHandler.on(notificationParams.notificationGroup, 'busy.notifications', (event) => {
		Manipulator.setAria(event.target, 'busy', event.busy);

		if (event.busy) {
			if (notificationParams.dropdownTrigger) {
				notificationParams.dropdownTrigger.dataset.loading = '';
			} else {
				Spinner.addTo(event.target);
			}

		} else {
			if (notificationParams.dropdownTrigger) {
				delete notificationParams.dropdownTrigger.dataset.loading;
			} else {
				Spinner.removeFrom(event.target);
			}
		}
	});
};

/**
 *
 * @returns {Promise<void>}
 */
const clearNotifications = async () => {
	const items = SelectorEngine.find('[data-notification]');

	for (const item of items) {
		item.remove();
	}
};

/**
 *
 * @returns {Promise<void>}
 */
const clearNotificationHint = async () => {
	const element = SelectorEngine.findOne('[data-notification-hint]');

	if (element) {
		element.remove();
	}
};

/**
 *
 * @param busy
 */
const loadingNotifications = (busy = true) => {
	EventHandler.trigger(notificationParams.notificationGroup, 'busy.notifications', {
		busy: busy
	});
};

/**
 *
 * @param data
 * @returns {Promise<void>}
 */
const treatNotificationHint = async (data) => {
	const currentHint = SelectorEngine.findOne('[data-notification-hint]');

	let writeHint = true;

	if (currentHint) {
		if (currentHint.dataset.notificationHint === data.id) {
			writeHint = false;
		} else {
			currentHint.remove();
		}
	}

	if (notificationParams.dropdown && writeHint) {
		// Text-CleanUp
		let teaser       = document.createElement('div');
		teaser.innerHTML = data.text;

		// Notification hint element
		const newHint = Manipulator.elementBefore(
			parseString(
				notificationHintTemplate,
				{
					date  : data.date || '',
					id    : data.id,
					teaser: teaser.textContent || teaser.innerText || '...',
					title : data.title || '?'
				}
			),
			notificationParams.dropdown
		);

		const button = SelectorEngine.findOne('button', newHint);
		const targetButton = SelectorEngine.findOne(`[data-notification="${data.id}"] button[data-details]`);

		if (button && targetButton) {
			EventHandler.on(button, 'click.notificationHint', (event) => {
				event.preventDefault();
				event.stopPropagation();

				EventHandler.trigger(targetButton, 'click.notification');
			});
		} else if (button) {
			button.remove();
		}
	}
}

/**
 * Benachrichtigungen zusammenstellen
 */
const treatNotifications = async (data) => {
	await clearNotifications();

	for (let index in data) {
		//
		// Notification (HTML element) zusammenstellen
		//

		const rid       = getUid('');
		const id        = data[index].id;
		const isRead    = (notificationParams.currentStorageData[id]?.read === true);
		const isRemoved = (notificationParams.currentStorageData[id]?.removed === true);

		if (!isRemoved) {
			// Text-CleanUp
			let teaser       = document.createElement('div');
			teaser.innerHTML = data[index].text;

			// Notification data
			const notificationData = {
				date  : data[index].date || '',
				id    : id,
				rid   : rid,
				read  : (isRead) ? 'true' : 'false',
				teaser: teaser.textContent || teaser.innerText || '...',
				text  : data[index].text || '',
				title : data[index].title || '?'
			};

			// Notification element
			Manipulator.elementAppend(
				parseString(notificationTemplate, notificationData),
				notificationParams.notificationGroup
			)
		}
	}

	await parseNotifications();

	await updateAllStorageData();

	loadingNotifications(false);

	await treatNotificationHint(data[0]);
}

/**
 * Keine Benachrichtigungen vorhanden
 */
const treatEmptyNotifications = async () => {
	await clearNotifications();

	loadingNotifications(false);
}

/**
 * Fehler bei Benachrichtigungen vorhanden
 */
const treatErrorNotifications = async () => {
	await clearNotifications();

	loadingNotifications(false);
}

/**
 * Web worker initialisieren
 *
 * @returns {Promise<void>}
 */
const setupNotificationWorker = async () => {
	// Worker-Instanz
	notificationWorker = new Worker('./ww-notifications.js');

	// Worker: Event, Daten empfangen
	notificationWorker.addEventListener('message', event => {
		// console.log('Worker Event ´message´');

		if (event.data?.type === 'loading') {
			loadingNotifications(true);
		} else if (event.data?.type === 'loaded') {
			if (event.data?.notifications?.length > 0) {
				treatNotifications(event.data?.notifications);
			} else {
				treatEmptyNotifications();
				clearNotificationHint();
			}
		} else if (event.data?.type === 'error') {
			// treatErrorNotifications();
			treatEmptyNotifications();
			clearNotificationHint();
		}
	});

	// Worker: Event, Fehler aufgetreten
	notificationWorker.addEventListener('error', event => {
		// console.log('Error from worker', event);
		treatErrorNotifications();
	});

	// Worker: starten
	notificationWorker.postMessage({
		type : 'connect',
		delay: 5, // Minutes (0 = off)
		// api      : './notifications.json',
		api      : './prGetInfomessage',
		immediate: true
	});
};

// -------
// Public
// -------

/**
 *
 * @returns {Promise<void>}
 */
const notifications = async () => {
	const pageContext = Manipulator.getDataAttribute(document.documentElement, 'page-context');

	// Hole alle DOM-Referenzen zu ´notification group´
	const notificationsDropdown = SelectorEngine.findOne('.dropdown-notifications');

	// Abbruch wenn...
	// - Seite LogIn oder LogOut ist
	// - Webwoker-Fetaure nicht existiert
	// - keine Dom-Elemente bzgl. ´notification group´ vorhanden
	if (['login', 'logout'].includes(pageContext) || !feature.webWorker || notificationsDropdown.length <= 0) {
		return;
	}

	notificationParams.dropdown = notificationsDropdown;
	notificationParams.dropdownTrigger = SelectorEngine.findOne('[data-bs-toggle="dropdown"]', notificationsDropdown);
	notificationParams.notificationGroup = SelectorEngine.findOne('[data-notification-group]', notificationsDropdown);

	// Berechtigungsabfrage bzgl. ´Browserbenachrichtigung´ beim Nutzer erfragen.
	if (feature.notification) {
		await Notification.requestPermission().then((result) => {
			window.Prins.config.notifications.granted = (result === 'granted');
		});
	} else {
		window.Prins.config.notifications.granted = false;
	}

	// Aktuelle im Browser gespeicherte Daten holen.
	notificationParams.currentStorageData = await storageDataGet();

	// Vorbereitung ´notification groups´ starten
	await setupNotificationGroup();

	// ´Notification worker´ starten
	await setupNotificationWorker();
};

// Export
export default notifications;
